"use client";

/**
 * AdminCheckboxFilter — 체크박스 다중 선택 필터
 *
 * 옵션 목록 중 여러 개를 체크박스로 선택하는 필터 필드.
 * AdminFilterField(라벨 좌 / 컨트롤 우)를 품고 있어 AdminFilter 안에 바로 넣으면 된다.
 * 제어 컴포넌트이며, 선택된 값 배열/콜백만 받는다. (전체 선택 기능 없음)
 *
 * options 를 서버에서 받아와 채우는 경우, 로딩 중엔 loading 을 켜서
 * 옵션이 없다가 갑자기 나타나는 깜빡임 대신 스켈레톤을 보여줄 수 있다.
 *
 * @example
 * <AdminCheckboxFilter
 *   label="권한"
 *   options={[
 *     { value: "admin", label: "관리자" },
 *     { value: "manager", label: "매니저" },
 *   ]}
 *   value={filters.roles}
 *   onChange={(v) => setField("roles", v)}
 *   loading={isOptionsLoading}
 * />
 */
import { useId, type ReactNode } from "react";

import { cn } from "../../lib/utils";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { AdminFilterField } from "./admin-filter";

export interface AdminCheckboxFilterOption {
  value: string;
  label: ReactNode;
  disabled?: boolean;
}

export interface AdminCheckboxFilterProps {
  /** 필드 라벨 */
  label?: ReactNode;
  options: AdminCheckboxFilterOption[];
  /** 선택된 값 배열 (기본 []) */
  value?: string[];
  onChange: (value: string[]) => void;
  /** 옵션 로딩 중 표시 (기본 false). 켜지면 옵션 대신 스켈레톤을 보여준다 */
  loading?: boolean;
  /** 로딩 스켈레톤 개수 (기본 3) */
  skeletonCount?: number;
  /** 옵션 id 접두사. 안 주면 인스턴스마다 useId()로 자동 생성되어 겹칠 일이 없다 */
  htmlFor?: string;
  className?: string;
}

export function AdminCheckboxFilter({
  label,
  options,
  value = [],
  onChange,
  loading = false,
  skeletonCount = 3,
  htmlFor,
  className,
}: AdminCheckboxFilterProps) {
  const autoId = useId();
  const baseId = htmlFor ?? autoId;

  const toggle = (v: string, checked: boolean) =>
    onChange(checked ? [...value, v] : value.filter((item) => item !== v));

  if (loading) {
    return (
      <AdminFilterField label={label} htmlFor={baseId} className={className}>
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: skeletonCount }).map((_, i) => (
            <div
              key={i}
              className="h-5 w-16 animate-pulse rounded bg-muted"
            />
          ))}
        </div>
      </AdminFilterField>
    );
  }

  return (
    <AdminFilterField label={label} htmlFor={baseId} className={className}>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {options.map((opt) => {
          const id = `${baseId}-${opt.value}`;
          return (
            <Label
              key={opt.value}
              htmlFor={id}
              className={cn(
                "flex items-center gap-1.5 font-normal",
                opt.disabled && "cursor-not-allowed opacity-50"
              )}
            >
              <Checkbox
                id={id}
                checked={value.includes(opt.value)}
                disabled={opt.disabled}
                onCheckedChange={(checked) => toggle(opt.value, checked === true)}
              />
              {opt.label}
            </Label>
          );
        })}
      </div>
    </AdminFilterField>
  );
}
